import React from 'react';
import Icon from '../../../components/AppIcon';

const ProgressTracker = ({ agentMetrics, selectedRole }) => {
  const filteredAgents = selectedRole === 'all'
    ? agentMetrics
    : agentMetrics?.filter(agent => agent?.role === selectedRole);

  const certifications = [
    { name: 'VCS Methodology Expert', status: 'completed', date: '2025-12-15' },
    { name: 'Gold Standard Validator', status: 'in-progress', progress: 65 },
    { name: 'Article 6 Compliance', status: 'not-started' },
    { name: 'MRV Specialist Certification', status: 'completed', date: '2025-11-20' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/10 text-green-600 border-green-200';
      case 'in-progress': return 'bg-yellow-500/10 text-yellow-600 border-yellow-200';
      case 'not-started': return 'bg-muted text-muted-foreground border-border';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'in-progress': return 'Clock';
      case 'not-started': return 'Circle';
      default: return 'Circle';
    }
  };

  const calculateTeamAverage = (metric) => {
    if (!filteredAgents?.length) return 0;
    const sum = filteredAgents?.reduce((acc, agent) => acc + (agent?.[metric] || 0), 0);
    return (sum / filteredAgents?.length)?.toFixed(1);
  };

  return (
    <div className="space-y-6">
      {/* Team Overview */}
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-4">Team Progress Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Icon name="Target" size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Accuracy</p>
                <p className="text-2xl font-bold text-foreground">{calculateTeamAverage('accuracyScore')}%</p>
              </div>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${calculateTeamAverage('accuracyScore')}%` }} />
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                <Icon name="CheckCircle" size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Completion</p>
                <p className="text-2xl font-bold text-foreground">{calculateTeamAverage('completionRate')}%</p>
              </div>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div className="h-1.5 rounded-full bg-green-500" style={{ width: `${calculateTeamAverage('completionRate')}%` }} />
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
                <Icon name="Award" size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Success Rate</p>
                <p className="text-2xl font-bold text-foreground">{calculateTeamAverage('successRate')}%</p>
              </div>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div className="h-1.5 rounded-full bg-purple-500" style={{ width: `${calculateTeamAverage('successRate')}%` }} />
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
                <Icon name="Zap" size={20} className="text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Response</p>
                <p className="text-2xl font-bold text-foreground">{calculateTeamAverage('avgResponseTime')}s</p>
              </div>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div className="h-1.5 rounded-full bg-orange-500" style={{ width: '70%' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Individual Agent Progress */}
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-4">Individual Agent Progress</h2>
        <div className="space-y-3">
          {filteredAgents?.map(agent => (
            <div key={agent?.id} className="bg-card border border-border rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{agent?.name}</h3>
                    <p className="text-sm text-muted-foreground capitalize">{agent?.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Analyses</p>
                    <p className="font-semibold text-foreground">{agent?.totalAnalyses}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Active</p>
                    <p className="font-semibold text-foreground">{agent?.activeConversations}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Accuracy</span>
                    <span className="text-xs font-medium text-foreground">{agent?.accuracyScore?.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${agent?.accuracyScore}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Completion</span>
                    <span className="text-xs font-medium text-foreground">{agent?.completionRate?.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-green-500" style={{ width: `${agent?.completionRate}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Success Rate</span>
                    <span className="text-xs font-medium text-foreground">{agent?.successRate?.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-purple-500" style={{ width: `${agent?.successRate}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Response</span>
                    <span className="text-xs font-medium text-foreground">{agent?.avgResponseTime}s</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-orange-500" style={{ width: '70%' }} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Certifications */}
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-4">Certification Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {certifications?.map((cert, idx) => (
            <div key={idx} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3 flex-1">
                  <Icon name={getStatusIcon(cert?.status)} size={20} className="text-primary mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-1">{cert?.name}</h3>
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium border ${getStatusColor(cert?.status)}`}>
                      {cert?.status?.replace('-', ' ')?.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
              {cert?.status === 'completed' && cert?.date && (
                <p className="text-xs text-muted-foreground">Completed: {cert?.date}</p>
              )}
              {cert?.status === 'in-progress' && cert?.progress && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Progress</span>
                    <span className="text-xs font-medium text-foreground">{cert?.progress}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-primary" style={{ width: `${cert?.progress}%` }} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {filteredAgents?.length === 0 && (
        <div className="text-center py-12 bg-muted/30 rounded-lg">
          <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No agent data available for this role</p>
        </div>
      )}
    </div>
  );
};

export default ProgressTracker;